import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { ScheduleEntryStatus } from "./DetailedItem";
import {
  floatingToDate,
  SCHEDULER_BOOKING_END_HOUR,
  SCHEDULER_BOOKING_START_HOUR,
  SCHEDULER_TIMEZONE,
} from "./config";

// Extend dayjs with comparison plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

/**
 * Maps day of week strings to dayjs day numbers (0 = Sunday, 1 = Monday, etc.)
 */
const DAY_OF_WEEK_MAP = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

/**
 * Checks if a given date/time slot is within a stylist's availability
 * @param {Object} stylist - The stylist object containing availability array
 * @param {Date|string} date - The date to check
 * @param {Date|string} time - The specific time to check (optional, if not provided checks whole day)
 * @returns {boolean} - True if stylist is available, false otherwise
 */
export const isStylistAvailable = (stylist, date, time = null) => {
  // If stylist has no availability data, assume they're available (backward compatibility)
  if (!stylist?.availability || !Array.isArray(stylist.availability) || stylist.availability.length === 0) {
    return true;
  }

  const checkDate = dayjs(date);
  const dayOfWeek = checkDate.day(); // 0 = Sunday, 1 = Monday, etc.

  // Find availability for this day of week
  const dayAvailability = stylist.availability.find((avail) => {
    const availDay = DAY_OF_WEEK_MAP[avail.day_of_week];
    return availDay === dayOfWeek;
  });

  // If no availability defined for this day, stylist is not available
  if (!dayAvailability) {
    return false;
  }

  // If we're only checking the day (not specific time), return true if day is available
  if (!time) {
    return true;
  }

  // Check if the specific time is within the availability window
  const checkTime = dayjs(time);
  
  // Parse the start and end times from availability (format: "HH:mm:ss")
  const [startHour, startMinute] = dayAvailability.start_time.split(':').map(Number);
  const [endHour, endMinute] = dayAvailability.end_time.split(':').map(Number);
  
  // Create dayjs objects for comparison (same date, different times)
  const availabilityStart = checkDate.hour(startHour).minute(startMinute).second(0);
  const availabilityEnd = checkDate.hour(endHour).minute(endMinute).second(0);
  
  // Check if the time falls within the availability window
  return checkTime.isSameOrAfter(availabilityStart) && checkTime.isBefore(availabilityEnd);
};

export const STYLIST_AVAILABILITY_ERROR =
  "The selected time is outside the stylist's available hours. Please choose a different time.";

/** False for 0-minute finishing markers — they must not occupy the stylist. */
export function isBlockingTimeRange(start, end) {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return Number.isFinite(s) && Number.isFinite(e) && e > s;
}

export function isZeroDurationEvent(item) {
  if (item?.isZeroDuration) return true;
  const start = item?.services?.[0]?.serviceStart ?? item?.start;
  const end = item?.services?.[0]?.serviceEnd ?? item?.end;
  return !isBlockingTimeRange(start, end);
}

/**
 * Events drawn on the scheduler grid.
 * 0-minute finishing blocks stay in appointment details.
 * Cancelled appointments stay in the client's history, not on the grid.
 */
export function visibleSchedulerEvents(events) {
  return (events || []).filter(
    (event) =>
      !isZeroDurationEvent(event) &&
      event?.appointment_status !== ScheduleEntryStatus.Canceled
  );
}

/* ---------------------------------------------------------------------------
 * Stylist time off (JD-465). Blocks come from the scheduler dataset as
 * wall-clock strings "YYYY-MM-DD HH:mm:ss" covering [start, end), and are
 * placed on the grid the same way appointment times are (floatingToDate).
 * ------------------------------------------------------------------------- */

export const STYLIST_TIME_OFF_ERROR = "Stylist is on time off during this time.";

/** Grey distinct from every service-category colour. */
export const TIME_OFF_EVENT_COLOR = "#9e9e9e";

const toMs = (value) => (value == null ? NaN : dayjs(value).valueOf());

/** A block's start/end as scheduler Dates (salon wall clock). */
export function timeOffBounds(block) {
  return {
    start: floatingToDate(block?.start, SCHEDULER_TIMEZONE),
    end: floatingToDate(block?.end, SCHEDULER_TIMEZONE),
  };
}

/**
 * The stylist's time-off block that [start, end) overlaps (half-open), or
 * null. `previous` ({ userId, start, end }) is the stored stylist and span
 * of an appointment being edited. When it is not moved (same stylist, same
 * start), blocks the stored span already overlapped are ignored, matching
 * the API: an appointment left inside time off can still be edited (its
 * services, and so its end), but a changed start or stylist is a move, and
 * nothing can be moved into time off, not even within the same block.
 */
export function findTimeOffOverlap(timeOff, userId, start, end, previous = null) {
  const startMs = toMs(start);
  const endMs = toMs(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;

  const prev =
    previous &&
    String(previous.userId) === String(userId) &&
    toMs(previous.start) === startMs
      ? { start: toMs(previous.start), end: toMs(previous.end) }
      : null;

  return (
    (timeOff || []).find((block) => {
      if (String(block.user_id) !== String(userId)) return false;
      const { start: blockStart, end: blockEnd } = timeOffBounds(block);
      if (!blockStart || !blockEnd) return false;
      const bs = blockStart.getTime();
      const be = blockEnd.getTime();
      if (!(startMs < be && bs < endMs)) return false;
      return !(prev && prev.start < be && bs < prev.end);
    }) || null
  );
}

/**
 * Minutes of [windowStart, windowEnd) the stylist is on time off
 * (overlapping blocks counted once).
 */
export function timeOffMinutesWithin(timeOff, userId, windowStart, windowEnd) {
  const ws = toMs(windowStart);
  const we = toMs(windowEnd);
  if (!Number.isFinite(ws) || !Number.isFinite(we) || we <= ws) return 0;

  const pieces = (timeOff || [])
    .filter((block) => String(block.user_id) === String(userId))
    .map((block) => {
      const { start, end } = timeOffBounds(block);
      return start && end
        ? [Math.max(ws, start.getTime()), Math.min(we, end.getTime())]
        : null;
    })
    .filter((piece) => piece && piece[1] > piece[0])
    .sort((a, b) => a[0] - b[0]);

  let total = 0;
  let cursor = ws;
  for (const [s, e] of pieces) {
    const from = Math.max(s, cursor);
    if (e > from) {
      total += e - from;
      cursor = e;
    }
  }
  return total / 60000;
}

/**
 * Scheduler events for time-off blocks. A block is split into one event per
 * calendar day, clipped to the grid's visible hours, so whole and multi-day
 * blocks are drawn in each day's column rather than the library's all-day
 * row. The events are read-only and open the time-off viewer when clicked
 * (not `disabled`: that would swallow the click).
 */
export function timeOffToEvents(timeOff) {
  const events = [];
  for (const block of timeOff || []) {
    const blockStart = dayjs.utc(String(block.start).replace(" ", "T"));
    const blockEnd = dayjs.utc(String(block.end).replace(" ", "T"));
    if (
      !blockStart.isValid() ||
      !blockEnd.isValid() ||
      !blockEnd.isAfter(blockStart)
    ) {
      continue;
    }

    for (
      let day = blockStart.startOf("day");
      day.isBefore(blockEnd);
      day = day.add(1, "day")
    ) {
      const visibleStart = day.hour(SCHEDULER_BOOKING_START_HOUR);
      const visibleEnd = day.hour(SCHEDULER_BOOKING_END_HOUR);
      const pieceStart = blockStart.isAfter(visibleStart)
        ? blockStart
        : visibleStart;
      const pieceEnd = blockEnd.isBefore(visibleEnd) ? blockEnd : visibleEnd;
      if (!pieceEnd.isAfter(pieceStart)) continue;

      events.push({
        event_id: `timeoff-${block.id}-${day.format("YYYYMMDD")}`,
        timeOffId: block.id,
        timeOff: block,
        isTimeOff: true,
        title: "Time off",
        subtitle: block.note || "",
        start: floatingToDate(
          pieceStart.format("YYYY-MM-DDTHH:mm:ss"),
          SCHEDULER_TIMEZONE
        ),
        end: floatingToDate(
          pieceEnd.format("YYYY-MM-DDTHH:mm:ss"),
          SCHEDULER_TIMEZONE
        ),
        user_id: block.user_id,
        editable: false,
        deletable: false,
        draggable: false,
        color: TIME_OFF_EVENT_COLOR,
      });
    }
  }
  return events;
}

function getAvailabilityEnd(stylist, date) {
  const dayAvailability = getStylistDayAvailability(stylist, date);
  if (!dayAvailability) {
    return null;
  }

  const checkDate = dayjs(date);
  const [endHour, endMinute] = dayAvailability.end_time.split(":").map(Number);
  return checkDate.hour(endHour).minute(endMinute).second(0);
}

/**
 * Builds booked segment windows from service step durations (same logic as CustomEditor).
 * @returns {{ busyTimes: { start: import("dayjs").Dayjs, end: import("dayjs").Dayjs }[], appointmentEnd: import("dayjs").Dayjs }}
 */
export function buildServiceBusyTimes(scheduleStart, services, serviceSteps) {
  let cursor = dayjs(scheduleStart);
  const busyTimes = [];

  for (const service of services) {
    const stepDetails = serviceSteps.find(
      (step) => step.service_id.toString() === service.service_id.toString()
    );
    if (!stepDetails) {
      continue;
    }

    const step1End = cursor.add(
      parseInt(stepDetails.service_step1_minutes, 10),
      "minute"
    );
    busyTimes.push({ start: cursor, end: step1End });

    cursor = step1End.add(
      parseInt(stepDetails.service_step2_minutes, 10) || 0,
      "minute"
    );

    if (stepDetails.service_step3_minutes) {
      const step3Minutes =
        parseInt(stepDetails.service_step3_minutes, 10) || 0;
      const step3End = cursor.add(step3Minutes, "minute");
      if (isBlockingTimeRange(cursor, step3End)) {
        busyTimes.push({ start: cursor, end: step3End });
      }
      cursor = step3End;
    }
  }

  return { busyTimes, appointmentEnd: cursor };
}

/**
 * Validates that an appointment start and end fit within stylist availability.
 * Reuses isStylistAvailable for the start-time check used when editing appointments.
 */
export function validateStylistAvailabilityForAppointment(stylist, start, end) {
  if (!stylist) {
    return { valid: true };
  }

  if (!isStylistAvailable(stylist, start, start)) {
    return { valid: false, message: STYLIST_AVAILABILITY_ERROR };
  }

  const availabilityEnd = getAvailabilityEnd(stylist, start);
  if (availabilityEnd && dayjs(end).isAfter(availabilityEnd)) {
    return { valid: false, message: STYLIST_AVAILABILITY_ERROR };
  }

  return { valid: true };
}

/**
 * Gets the availability for a stylist on a specific day
 * @param {Object} stylist - The stylist object containing availability array
 * @param {Date|string} date - The date to check
 * @returns {Object|null} - The availability object for that day, or null if not available
 */
export const getStylistDayAvailability = (stylist, date) => {
  if (!stylist?.availability || !Array.isArray(stylist.availability) || stylist.availability.length === 0) {
    return null;
  }

  const checkDate = dayjs(date);
  const dayOfWeek = checkDate.day();

  return stylist.availability.find((avail) => {
    const availDay = DAY_OF_WEEK_MAP[avail.day_of_week];
    return availDay === dayOfWeek;
  }) || null;
};

/**
 * Checks if a time slot (cell) should be greyed out for a stylist
 * This is the main function to use for cell rendering
 * @param {Object} stylist - The stylist object
 * @param {Date} cellDate - The date/time of the cell
 * @returns {boolean} - True if cell should be greyed out (unavailable)
 */
export const isCellUnavailable = (stylist, cellDate) => {
  return !isStylistAvailable(stylist, cellDate, cellDate);
};
